import { clamp, range } from 'lodash-es';
import { useMemo } from 'react';
import { scrollbarSize } from 'utils/dom-helpers';

export interface RenderRowProps {
  rowIndex: number;
  key: string | number;
  style: { height: number; top: number; position: 'absolute'; right: 0; left: 0 };
}

interface Props {
  scrollTop: number;
  height: number;
  rowCount: number;
  getRowHeight: (index: { index: number }) => number;
  /**
   * Approximate height of rows that have not been rendered yet.
   * Avoids calculating every single row height every time heights change
   */
  estimatedRowHeight: number;
}
export const useVirtualizedRows = ({
  scrollTop,
  height: containerHeight,
  rowCount,
  getRowHeight,
  estimatedRowHeight,
}: Props) => {
  const { getRowTop, getMaxCalculatedOffset } = useMemo(() => {
    const offsets: Record<number, number> = { 0: 0 };
    let maxCalculatedOffset = 0;
    const getMaxCalculatedOffset = () => maxCalculatedOffset;
    const getRowTop = (index: number) => {
      if (index > maxCalculatedOffset) {
        for (let i = maxCalculatedOffset + 1; i <= index; i++) {
          offsets[i] = offsets[i - 1] + getRowHeight({ index: i - 1 });
        }
        maxCalculatedOffset = index;
      }
      return offsets[index];
    };
    return { getRowTop, getMaxCalculatedOffset };
  }, [getRowHeight]);

  const rowIndexes = useMemo(() => {
    let startRowOffset = 0;
    while (startRowOffset < rowCount && getRowTop(startRowOffset) <= scrollTop) {
      startRowOffset++;
    }

    let endRowOffset = startRowOffset;
    while (endRowOffset < rowCount && getRowTop(endRowOffset) < scrollTop + containerHeight) {
      endRowOffset++;
    }
    return range(clamp(startRowOffset - 1, 0, rowCount - 1), clamp(endRowOffset + 1, 0, rowCount));
  }, [getRowTop, containerHeight, scrollTop, rowCount]);

  // Only calculate the layout for the at or above the rows that are visible
  const maxCalculatedOffset = getMaxCalculatedOffset();
  const calculatedScrollHeight =
    getRowTop(maxCalculatedOffset) +
    (rowCount > 0 ? getRowHeight({ index: maxCalculatedOffset }) : 0) +
    (rowCount - getMaxCalculatedOffset() - 1) * estimatedRowHeight;
  const totalScrollHeight = Math.max(calculatedScrollHeight, containerHeight - scrollbarSize());

  const render = useMemo(
    () => (renderRow: (props: RenderRowProps) => React.ReactElement) =>
      rowIndexes.map(rowIndex => {
        const rowTop = getRowTop(rowIndex);
        let rowHeight = getRowHeight({ index: rowIndex });
        if (rowIndex >= rowCount - 1) {
          rowHeight = Math.max(rowHeight, totalScrollHeight - rowTop);
        }
        return renderRow({
          rowIndex,
          key: rowIndex,
          style: {
            position: 'absolute',
            top: rowTop,
            height: rowHeight,
            right: 0,
            left: 0,
          },
        });
      }),
    [rowIndexes, getRowHeight, getRowTop, rowCount, totalScrollHeight]
  );
  return { totalScrollHeight, rowIndexes, render, getRowTop };
};
